import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clapperboard, Film, ListVideo, CheckCircle2, FileText, ArrowRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#121218] border border-[#282834] rounded-xl p-5 hover:border-[#3a3a48] transition-colors duration-200" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <p className="font-head font-700 text-3xl text-[#F4F4F5]">{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 4)));
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="fade-up">
        <p className="text-[#00E5FF] text-sm uppercase tracking-widest">Sala de Edição</p>
        <h1 className="font-head font-700 text-4xl mt-1">Olá, {user?.name?.split(" ")[0]} 🎬</h1>
        <p className="text-[#A1A1AA] mt-2">Visão geral da produção do reality.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
        <StatCard icon={Clapperboard} label="Projetos" value={stats?.projects ?? "–"} accent="#FF2E93" />
        <StatCard icon={Film} label="Vídeos" value={stats?.videos ?? "–"} accent="#00E5FF" />
        <StatCard icon={ListVideo} label="Episódios" value={stats?.episodes ?? "–"} accent="#F59E0B" />
        <StatCard icon={FileText} label="Roteiros" value={stats?.scripts ?? "–"} accent="#00E5FF" />
        <StatCard icon={CheckCircle2} label="Aprovados" value={stats?.approved ?? "–"} accent="#10B981" />
      </div>

      <div className="flex items-center justify-between mt-12 mb-4">
        <h2 className="font-head font-600 text-xl">Projetos recentes</h2>
        <button
          data-testid="go-projects-button"
          onClick={() => navigate("/projects")}
          className="text-sm text-[#00E5FF] flex items-center gap-1 hover:gap-2 transition-all duration-200"
        >
          Ver todos <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="bg-[#121218] border border-dashed border-[#282834] rounded-xl p-12 text-center">
          <p className="text-[#A1A1AA]">Nenhum projeto ainda. Crie o primeiro na aba Projetos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p) => (
            <button
              key={p.id}
              data-testid={`dashboard-project-${p.id}`}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="text-left bg-[#121218] border border-[#282834] rounded-xl overflow-hidden hover:-translate-y-1 hover:border-[#FF2E93] transition-all duration-200 group"
            >
              <div className="h-28 relative overflow-hidden">
                <img src={p.thumbnail} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-200" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121218] to-transparent" />
              </div>
              <div className="p-4">
                <p className="font-head font-600 text-lg">{p.name}</p>
                <p className="text-sm text-[#A1A1AA] line-clamp-1">{p.description || "Sem descrição"}</p>
                <div className="flex gap-4 mt-3 text-xs text-[#A1A1AA]">
                  <span>{p.video_count} vídeos</span>
                  <span>{p.episode_count} episódios</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
